import { useStripe } from '@stripe/react-stripe-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { v1Api } from '@/api/backendApi';
import { useSidebarModalContext } from '@/components/Modals/Sidebar/context/SidebarModalContext';
import { toast } from '@music-vine/cadence/ui';
import useBillingMode from '@/hooks/billing/useBillingMode';
import { useGetApiV1SubscriptionStripeProration } from '@/open-api/subscription/subscription';
import { logEvent } from '@/pages/api/v1/utils';
import { systemKeys } from '@/utils/queryKeys';
import { clearConversion, getConversion } from '@/utils/analytics/conversion';
import { usePricingOptions } from '../api/usePricingOptions';
import { useSubscriptionType } from '../auth';
import { useSettings } from '../state/useSettings';
import type { Plan } from '@/fixtures/plans';
import type { AxiosError } from 'axios';
import { useState } from 'react';
import type { PricingOption } from '../api/usePricingOptions';
import { retryCheckUpgradeStatus } from './useStripeCheckoutResponse/retryCheckUpgradeStatus';
import type { Role } from '@/types/auth';
export enum ProrationBillingMode {
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}
interface UseProrationProps {
  billingMode: ProrationBillingMode;
  plan: Plan['name'];
}
export const useProration = ({
  plan,
  billingMode
}: UseProrationProps, {
  enabled = true
}: {
  enabled?: boolean;
} = {}) => {
  const {
    settings
  } = useSettings();
  const subscriptionType = useSubscriptionType();
  const currencyCode = settings?.currency.id ?? 2;
  const {
    data: pricingOptions,
    isLoading: isPricingOptionsLoading
  } = usePricingOptions();
  const pricingOption = pricingOptions?.find(option => option.title === plan);
  const premiumPlanId = pricingOption?.subscription_stripe_plan_id;

  // Mirror the resolution in update-subscription so the preview and the
  // commit target the same plan. Same-tier period switches stay on the
  // current plan; tier upgrades prorate from the current plan to the target.
  const prorationPlanId = plan === subscriptionType ? settings?.subscriptionData?.plan : premiumPlanId;
  return useGetApiV1SubscriptionStripeProration({
    SubscriptionStripePlan: prorationPlanId ?? 0,
    CurrencyCode: currencyCode,
    PlanIdentifier: billingMode === ProrationBillingMode.MONTHLY ? 1 : 2
  }, {
    query: {
      // Guard on `!== undefined` rather than truthiness so that the `?? 0`
      // params fallback (used only to satisfy the required-number type)
      // cannot accidentally disable the query.
      enabled: enabled && !isPricingOptionsLoading && prorationPlanId !== undefined
    }
  });
};
interface UpgradeSubscriptionProps extends UseProrationProps {
  planSubscriptionId: PricingOption['subscription_stripe_plan_id'];
  proration_date: string;
  value?: number | string;
}
interface UpgradeResponse {
  clientSecret?: string;
  message: string;
  success: boolean;
}
const UPGRADE_FAILED_MESSAGE = "We couldn't confirm your payment, so your plan hasn't changed. Please check your card details and try again.";
interface UseUpgradeSubscriptionOptions {
  /**
   * Run once the upgrade is complete, on both the plain and the 3D Secure
   * paths. Screens hosted by DialogStage pass their `close` here: a per-mutate
   * `onSuccess` never fires after a challenge, because the commit request
   * rejects with `requires_action` and completion happens from `onError`.
   */
  onComplete?: () => void;
}
export const useUpgradeSubscription = ({
  onComplete
}: UseUpgradeSubscriptionOptions = {}) => {
  const [isUpdatingRoles, setIsUpdatingRoles] = useState(false);
  const queryClient = useQueryClient();
  const {
    settings,
    invalidateSettings
  } = useSettings();
  const {
    onOpenChange
  } = useSidebarModalContext();
  const stripe = useStripe();
  const {
    asPath
  } = useRouter();
  const {
    setBillingMode
  } = useBillingMode();
  const plan = useSubscriptionType();
  const {
    data: pricingOptions
  } = usePricingOptions();
  const handle3DSecureAction = async (clientSecret: string) => {
    if (!stripe) {
      throw new Error('Stripe not initialised');
    }
    const result = await stripe.confirmCardPayment(clientSecret);
    if (result.error) {
      toast.error(result.error.message ?? UPGRADE_FAILED_MESSAGE);
      return false;
    }
    return true;
  };
  const completeUpgrade = async ({
    billingMode,
    value,
    plan: targetPlan
  }: UpgradeSubscriptionProps) => {
    const roleExpectedAfterUpgrade = pricingOptions?.find(option => option.title === targetPlan);
    setIsUpdatingRoles(true);

    // A 2xx or a passed 3DS challenge doesn't guarantee the invoice was paid,
    // so only the role actually changing counts as an upgrade.
    const {
      success
    } = await retryCheckUpgradeStatus({
      maxRetries: 10,
      throwOnError: false,
      expectedRole: roleExpectedAfterUpgrade?.role as Role ?? 'Creator',
      userId: settings?.user?.id
    });
    setIsUpdatingRoles(false);
    invalidateSettings();
    queryClient.invalidateQueries({
      queryKey: ['auth']
    });
    if (!success) {
      toast.error(UPGRADE_FAILED_MESSAGE);
      return;
    }
    setBillingMode(billingMode);
    const {
      conversion_source,
      conversion_path,
      conversion_asset_type,
      conversion_asset_id
    } = getConversion();
    void logEvent({
      event_type: 'purchase',
      asPath,
      metadata: {
        type: 'upgrade',
        plan_length: billingMode,
        value: Number(value) || 0,
        plan_type: targetPlan,
        currency: settings?.currency.code,
        conversion_source,
        conversion_path,
        conversion_asset_type,
        conversion_asset_id
      }
    });
    clearConversion();
    toast.success('Subscription upgraded successfully');
    void queryClient.invalidateQueries({
      queryKey: systemKeys.currentPrice()
    });
    onOpenChange(false);
    onComplete?.();
  };
  const upgradeSubscriptionMutation = useMutation({
    mutationFn: async ({
      billingMode,
      proration_date,
      planSubscriptionId
    }: UpgradeSubscriptionProps) => v1Api.post<UpgradeResponse>('v2/subscription/update-subscription', {
      subscription_stripe_plan: planSubscriptionId,
      currency_id: settings?.currency.id ?? 2,
      plan_identifier: billingMode === ProrationBillingMode.MONTHLY ? 1 : 2,
      proration_date
    }),
    onSuccess: async ({
      data
    }, variables) => {
      if (data?.success === false) {
        toast.error(data.message || UPGRADE_FAILED_MESSAGE);
        invalidateSettings();
        return;
      }
      await completeUpgrade(variables);
    },
    onError: async (error: AxiosError<UpgradeResponse>, variables) => {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.warn(error.response.data);
        console.warn(error.response.status);
        console.warn(error.response.headers);
        if (typeof error.response.data === 'string' && error.response.data) {
          toast.error(error.response.data);
        } else if (error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
          if (error.response.data.message === 'requires_action') {
            if (error.response.data.clientSecret) {
              const success = await handle3DSecureAction(error.response.data.clientSecret);
              if (success) {
                await completeUpgrade(variables);
              } else {
                invalidateSettings();
              }
            } else {
              toast.error('Your bank needs to confirm this payment, but we could not start that check. Please try again.');
            }
            return;
          }
          toast.error((error.response.data as {
            message: string;
          }).message || `${error.response.status}: Something went wrong. Please contact support via our help center if the problem persists.`);
        } else {
          toast.error('Subscription upgrade failed.');
        }
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        console.warn(error.request);
        toast.error('Could not upgrade subscription. No response from server.');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.warn('Error', error.message);
        toast.error(error.message);
      }
      console.warn(error.config);
    }
  });
  return {
    upgradeSubscriptionMutation,
    isUpdatingRoles
  };
};